import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { AdminSupportCenterService } from '../services/admin-support-center.service';

@Component({
  selector: 'app-admin-support-center',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NzButtonModule,
    NzCardModule,
    NzInputModule,
    NzModalModule,
    NzSelectModule,
    NzTableModule,
    NzTabsModule
  ],
  templateUrl: './admin-support-center.component.html',
  styleUrl: './admin-support-center.component.less'
})
export class AdminSupportCenterComponent implements OnInit {
  supportRequests: any[] = [];
  aiInteractions: any[] = [];
  loadingSupport = false;
  loadingAi = false;
  activeTab = 0;
  supportTypeFilter = '';
  supportStatusFilter = '';
  supportSearch = '';
  aiFeedbackFilter = '';
  aiSearch = '';
  detailVisible = false;
  detailType: 'support' | 'ai' = 'support';
  selectedItem: any = null;

  readonly supportStatuses = ['open', 'in_progress', 'resolved', 'closed'];

  constructor(
    private readonly adminSupportCenterService: AdminSupportCenterService,
    private readonly message: NzMessageService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadSupportRequests();
    this.loadAiInteractions();
  }

  loadSupportRequests(): void {
    this.loadingSupport = true;
    this.adminSupportCenterService.getSupportRequests({
      type: this.supportTypeFilter || undefined,
      status: this.supportStatusFilter || undefined
    }).subscribe({
      next: (items) => {
        this.supportRequests = items || [];
        this.loadingSupport = false;
      },
      error: () => {
        this.loadingSupport = false;
        this.message.error(this.translate.instant('ADMIN.SUPPORT_CENTER.LOAD_ERROR'));
      }
    });
  }

  loadAiInteractions(): void {
    this.loadingAi = true;
    this.adminSupportCenterService.getAiInteractions({
      feedback: this.aiFeedbackFilter || undefined
    }).subscribe({
      next: (items) => {
        this.aiInteractions = items || [];
        this.loadingAi = false;
      },
      error: () => {
        this.loadingAi = false;
        this.message.error(this.translate.instant('ADMIN.SUPPORT_CENTER.LOAD_ERROR'));
      }
    });
  }

  openSupportDetails(id: string): void {
    this.adminSupportCenterService.getSupportRequest(id).subscribe({
      next: (item) => {
        this.selectedItem = item;
        this.detailType = 'support';
        this.detailVisible = true;
      },
      error: () => this.message.error(this.translate.instant('ADMIN.SUPPORT_CENTER.LOAD_ERROR'))
    });
  }

  openAiDetails(id: string): void {
    this.adminSupportCenterService.getAiInteraction(id).subscribe({
      next: (item) => {
        this.selectedItem = item;
        this.detailType = 'ai';
        this.detailVisible = true;
      },
      error: () => this.message.error(this.translate.instant('ADMIN.SUPPORT_CENTER.LOAD_ERROR'))
    });
  }

  updateStatus(id: string, status: string): void {
    this.adminSupportCenterService.updateSupportRequestStatus(id, status).subscribe({
      next: () => {
        this.message.success(this.translate.instant('ADMIN.SUPPORT_CENTER.STATUS_UPDATED'));
        this.loadSupportRequests();
        if (this.selectedItem?._id === id) {
          this.selectedItem = { ...this.selectedItem, status };
        }
      },
      error: () => this.message.error(this.translate.instant('ADMIN.SUPPORT_CENTER.STATUS_UPDATE_ERROR'))
    });
  }

  closeDetails(): void {
    this.detailVisible = false;
    this.selectedItem = null;
  }

  get filteredSupportRequests(): any[] {
    const term = this.supportSearch.trim().toLowerCase();
    if (!term) {
      return this.supportRequests;
    }

    return this.supportRequests.filter((item) =>
      `${item.subject || ''} ${item.context?.user?.name || ''} ${item.context?.user?.email || ''} ${item.context?.route || ''} ${item.context?.pageTitle || ''}`
        .toLowerCase()
        .includes(term)
    );
  }

  get filteredAiInteractions(): any[] {
    const term = this.aiSearch.trim().toLowerCase();
    if (!term) {
      return this.aiInteractions;
    }

    return this.aiInteractions.filter((item) =>
      `${item.question || ''} ${item.answer || ''} ${item.context?.user?.email || ''} ${item.context?.route || ''}`
        .toLowerCase()
        .includes(term)
    );
  }

  getAnswerPreview(answer: string): string {
    const normalized = (answer || '').replace(/\s+/g, ' ').trim();
    if (normalized.length <= 120) {
      return normalized;
    }
    return `${normalized.slice(0, 117)}...`;
  }

  getSourceCount(item: any): number {
    return Array.isArray(item?.sources) ? item.sources.length : 0;
  }
}
